import '@renderer/browser/bootstrap-browser-runtime'
import { StrictMode, memo, useEffect, useRef } from 'react'
import { getAppRoot } from '@renderer/utils/app-root'
import * as Sentry from '@sentry/electron/renderer'
import { initSentryRenderer } from '@renderer/utils/sentry.renderer'
import '@renderer/styles/index.css'
import { IntlProvider } from '@renderer/utils/Intl/Intl.tsx'
import { useIsRendererReady } from '@renderer/hooks/useIsRendererReady'
import { useGlobalState } from '@highlight/ipc/react'
import ChatProviders from '@renderer/overlay/Chat/ChatProviders.tsx'
import { useIsOnboardingWalkthrough } from '@renderer/overlay/Assistant/assistant-atoms.ts'
import OverlayErrorBoundary from '@renderer/overlay/OverlayErrorBoundary.tsx'
import { OnboardingBackground } from '@renderer/overlay/Assistant/OnboardingBackground.tsx'
import Toaster from '@renderer/overlay/Toaster'
import { SidepanelOverlay } from '@renderer/overlay/Sidepanel'
import { Mini } from '@renderer/overlay/Mini'
import { StagedActionsProvider } from '@renderer/overlay/providers/staged-actions-provider'
import { StagedActionsGatewayProvider } from '@renderer/overlay/providers/staged-actions-gateway-provider'
import { RemindersGatewayProvider } from '@renderer/overlay/providers/reminders-gateway-provider'
import { ConversationsGatewayProvider } from '@renderer/overlay/providers/conversations-gateway-provider'
import { RemindersProvider } from '@renderer/overlay/providers/reminders-provider'
import { PylonProvider } from '@renderer/providers/PylonProvider'
import { AssistantHandlers } from '@renderer/overlay/AssistantHandlers'
import { AudioNoteSummaryHandler } from '@renderer/overlay/audio-note-summary-handler'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { useQuickTourActive } from '@renderer/overlay/Sidepanel/atoms'
import { useThemeListener } from '@renderer/hooks/useThemeListener'
import { AudioDebugOverlay } from '@renderer/overlay/components/audio-debug-overlay'
import { Interactive } from '@highlight/ui'
import { GlobalEvents, GlobalState } from '@highlight/ipc'
import { twMerge } from 'tailwind-merge'
import { useIsWindowFocused } from '@renderer/chat'
import { ChatInputElementId } from '@renderer/chat/components'
import { usePublishOverlayInputFocus } from '@renderer/overlay/hooks/use-publish-overlay-input-focus'
import { MiniAskInputElementId } from '@renderer/overlay/Mini/mini-ask-input'
import { useAppBadgeSync } from '@renderer/overlay/hooks/use-app-badge-sync'
import { useDetachedChatCompletionWatcher } from '@renderer/overlay/hooks/use-detached-chat-completion-watcher'
import { useRefreshFlagsOnSidepanelToggle } from '@renderer/overlay/hooks/use-refresh-flags-on-sidepanel-toggle'
import { listenForBeforeAudioNoteSave } from '@renderer/ipc/before-audio-note-save'
import { useIsOnboarded } from '@renderer/hooks/useIsOnboarded'
import { initRendererLogger } from '@renderer/utils/init-renderer-logger'
import { FlagsProvider } from '@highlight/analytics/react'
import { AnalyticsRenderer } from '@renderer/utils/analytics.renderer'

// Keep in sync with AudioNoteStateManager (main); shared GlobalState flag the
// overlay sets when its meeting handlers are mounted.
const OVERLAY_HANDLERS_READY_KEY = 'overlay.handlersReady'
const OVERLAY_TEXT_INPUT_IDS = [ChatInputElementId, MiniAskInputElementId]

const OverlayApp = () => {
  const isRendererReady = useIsRendererReady()

  if (!isRendererReady) {
    return null
  }

  return (
    <IntlProvider>
      <Interactive.Provider>
        <OverlayErrorBoundary>
          <ChatProviders>
            <PylonProvider>
              <StagedActionsGatewayProvider>
                <ConversationsGatewayProvider>
                  <RemindersGatewayProvider>
                    <RemindersProvider>
                      <StagedActionsProvider>
                        <OverlayContents />
                      </StagedActionsProvider>
                    </RemindersProvider>
                  </RemindersGatewayProvider>
                </ConversationsGatewayProvider>
              </StagedActionsGatewayProvider>
            </PylonProvider>
          </ChatProviders>
        </OverlayErrorBoundary>
      </Interactive.Provider>
    </IntlProvider>
  )
}

export default OverlayApp

const OverlayContents = memo(function OverlayContents() {
  const [showBackgroundOverlay] = useGlobalState<boolean>('assistant.showBackgroundOverlay', false)
  const isOnboardingWalkthrough = useIsOnboardingWalkthrough()
  const sidepanelState = useSidepanelState()
  const quickTourActive = useQuickTourActive()
  const isOnboarded = useIsOnboarded()
  const isWindowFocused = useIsWindowFocused()
  const wasWindowFocusedRef = useRef(isWindowFocused)
  // Keep sidepanel mounted during onboarding handoff so QuickTour can render
  // (the user store's `onboarded` flag only flips once the tour finishes)
  const shouldShowSidepanel = isOnboarded || quickTourActive

  useThemeListener()
  usePublishOverlayInputFocus(OVERLAY_TEXT_INPUT_IDS)
  useAppBadgeSync()
  useDetachedChatCompletionWatcher()
  useRefreshFlagsOnSidepanelToggle()

  useEffect(() => {
    const lostFocus = wasWindowFocusedRef.current && !isWindowFocused
    wasWindowFocusedRef.current = isWindowFocused
    if (!lostFocus) {
      return
    }

    // Main does not reliably receive BrowserWindow blur/AppKit deactivation
    // when focus was inside a webview. The renderer can still observe document
    // visibility, so forward that signal while leaving hide policy in main.
    void GlobalEvents.invoke('overlay:deactivate').catch((error) => {
      Sentry.captureException(error, {
        tags: { area: 'overlay', operation: 'deactivate' },
      })
    })
  }, [isWindowFocused])

  // Tell the main process the overlay's meeting handlers (AssistantHandlers +
  // Toaster, rendered below) are mounted and subscribed by flipping the shared
  // `overlay.handlersReady` flag. This effect runs after its children's effects,
  // so by the time it fires every meeting listener is attached. Main buffers any
  // meeting detected before the flag is true (e.g. Recall firing during startup)
  // and replays it once it flips, so a capture is never silently dropped due to
  // overlay timing.
  useEffect(() => {
    if (window.__RENDERER_NAME !== 'overlay') {
      return
    }

    const announceReady = () => {
      GlobalState.set(OVERLAY_HANDLERS_READY_KEY, true)
    }
    announceReady()

    // Clear the flag when the overlay goes away so main resumes buffering rather
    // than emitting into a renderer with no listeners (the exact drop this
    // prevents). The React cleanup covers handler unmounts (error-boundary
    // recovery); `beforeunload` covers full window teardown, where cleanup may
    // not run.
    const clearReady = () => {
      GlobalState.set(OVERLAY_HANDLERS_READY_KEY, false)
    }
    window.addEventListener('beforeunload', clearReady)
    return () => {
      window.removeEventListener('beforeunload', clearReady)
      clearReady()
    }
  }, [])

  return (
    <>
      {/* Global handlers stay mounted with the overlay window. */}
      <AssistantHandlers />
      <AudioNoteSummaryHandler />
      <div className={twMerge('h-full w-full', showBackgroundOverlay ? 'bg-dark-20' : 'bg-transparent')}>
        <Toaster />
        {isOnboardingWalkthrough && sidepanelState === 'open' && <OnboardingBackground />}
        {shouldShowSidepanel && <SidepanelOverlay />}
        {shouldShowSidepanel && <Mini />}
        <Interactive.Debugger />
        {/* Developer-only audio diagnostic overlay; see the component for the hotkey. */}
        <AudioDebugOverlay />
      </div>
    </>
  )
})

// Initialize Sentry
initRendererLogger()
initSentryRenderer()

// Answer main's pre-save flush request (the meeting notepad's pending writes).
listenForBeforeAudioNoteSave()

// Render the root React component
const root = getAppRoot(document.getElementById('app')!)
root.render(
  <StrictMode>
    <FlagsProvider flags={AnalyticsRenderer.flags}>
      <OverlayApp />
    </FlagsProvider>
  </StrictMode>,
)

// Ignore the ResizeObserver loop limit exceeded error.
// It's a benign error so don't need it in the logs.
// Documentation of window.onerror: https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event
const originalErrorHandler = window.onerror
window.onerror = function (event, source, lineno, colno, error) {
  if (event === 'ResizeObserver loop limit exceeded') {
    console.warn('Ignored: ResizeObserver loop limit exceeded')
    return true // This blocks event propagation
  }
  // Pass through any other errors to the original error handler
  if (typeof originalErrorHandler === 'function') {
    originalErrorHandler(event, source, lineno, colno, error)
  }
}
